import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Car, MapPin } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { formatBookingDateTime } from '@/lib/utils/dates'
import type { BookingDetail } from '@/lib/types/booking.types'

interface ConversationTripContextProps {
  booking: BookingDetail
  onPress: () => void
}

/**
 * A compact, scrollable conversation header. It anchors coordination to the
 * actual reservation without permanently consuming chat viewport height.
 */
export function ConversationTripContext({ booking, onPress }: ConversationTripContextProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const dateRange = `${formatBookingDateTime(booking.startAt, i18n.language)} — ${formatBookingDateTime(
    booking.endAt,
    i18n.language,
  )}`

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${t('messages.trip_context_open')}: ${booking.vehicleTitle}`}
      testID="conversation.trip-context"
      style={({ pressed }) => ({
        marginHorizontal: SPACING.md,
        marginTop: SPACING.sm,
        marginBottom: SPACING.xs,
        padding: SPACING.sm,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.11)' : 'rgba(26,21,37,0.08)',
        backgroundColor: colors.surface,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,90,95,0.12)',
          }}
        >
          <Car size={18} color={COLORS.coral.DEFAULT} strokeWidth={2.1} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}
          >
            {t('messages.trip_context_title')} · {booking.vehicleTitle}
          </Text>
          <Text
            numberOfLines={1}
            style={{
              marginTop: 1,
              fontFamily: FONTS.body,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {dateRange}
          </Text>
        </View>
        <ArrowRight size={18} color={colors.textSecondary} strokeWidth={2.2} />
      </View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs,
          marginTop: SPACING.xs,
        }}
      >
        <MapPin size={14} color={colors.textAccent} strokeWidth={2.2} />
        <Text
          numberOfLines={1}
          style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}
        >
          {booking.pickupAddress} → {booking.dropoffAddress}
        </Text>
      </View>
    </Pressable>
  )
}
